/* eslint-disable react-hooks/exhaustive-deps */
import { ReactElement, FC, useState, useEffect, useCallback } from 'react'
import Select from 'react-select'

import { useI18n } from '../../hooks'
import { getCountries, getInstitutions, getInstitutionWebStore, getProvinces } from '../../services'
import { ApiCountry, ApiInstitutions, ApiInstitutionWebstore, APiProvince } from '../../types'
import { TailLoader } from '../loaders'
import { openSnackbar } from '../snackbar'

import './findMySchoolForm.css'

const FindMySchoolForm: FC = (): ReactElement => {
  const { t } = useI18n()
  const msgToBeDisplay = {
    selectCountry: (
      <div>
        Get started. Please select your <span className="heavy">country</span>.
      </div>
    ),
    selectStateProvince: (
      <div>
        Please select your <span className="heavy">state/province</span>.
      </div>
    ),
    selectSchool: (
      <div>
        Please select your <span className="heavy">school</span>.
      </div>
    ),
    schoolNoWebStore: (
      <div>
        Your institution does not have a WebStore yet, but you can still get access discounts on <a href="/">OnTheHub.</a>
      </div>
    ),
    department: (
      <div>
        Please select a <span className="heavy">department</span>.
      </div>
    ),
    schoolWebstore: (webstore: SelectOption) => (
      <div>
        Visit the{' '}
        <a className="heavy" href={webstore.value}>
          {webstore.label} WebStore
        </a>{' '}
        to view all available offerings
      </div>
    ),
  }

  type SelectOption = { value: string; label: string }
  type ProvinceIncludedCountry = 'US' | 'CA'
  const DEFAULT_SELECTION = { value: '', label: '' }
  const [countryInput, setCountryInput] = useState<SelectOption>(DEFAULT_SELECTION)
  const [provinceInput, setProvinceInput] = useState<SelectOption>(DEFAULT_SELECTION)
  const [institutionInput, setInstitutionInput] = useState<SelectOption>(DEFAULT_SELECTION)
  const [departmentInput, setDepartmentInput] = useState<SelectOption>(DEFAULT_SELECTION)
  const [currentHint, setCurrentHint] = useState(msgToBeDisplay.selectCountry)
  const [countries, setCountries] = useState<ApiCountry[]>([])
  const [provinces, setProvinces] = useState<APiProvince[]>([])
  const [institutions, setInstitutions] = useState<ApiInstitutions[]>([])
  const [webstores, setWebstores] = useState<ApiInstitutionWebstore[]>([])

  const [serviceUnavailable, setServiceUnavailable] = useState<boolean>(false)

  const isProvinceIncludedCountry = useCallback(
    (equal = true): boolean =>
      equal
        ? countryInput.value === 'US' || countryInput.value === 'CA'
        : !!countryInput.value && countryInput.value !== 'US' && countryInput.value !== 'CA',
    [countryInput.value]
  )

  const handleCountryChange = (value: SelectOption | null) => {
    setCountryInput(value || DEFAULT_SELECTION)
    setProvinceInput(DEFAULT_SELECTION)
    setInstitutionInput(DEFAULT_SELECTION)
    setDepartmentInput(DEFAULT_SELECTION)
  }
  const handleProvinceChange = (value: SelectOption | null) => {
    setProvinceInput(value || DEFAULT_SELECTION)
    setInstitutionInput(DEFAULT_SELECTION)
    setDepartmentInput(DEFAULT_SELECTION)
  }
  const handleSchoolChange = (value: SelectOption | null) => {
    setInstitutionInput(value || DEFAULT_SELECTION)
    setDepartmentInput(DEFAULT_SELECTION)
  }
  const handleDepartmentChange = (value: SelectOption | null) => {
    setDepartmentInput(value || DEFAULT_SELECTION)
  }

  const fetchCountries = useCallback(async () => {
    try {
      const { data } = await getCountries()
      setCountries(data)
    } catch (e) {
      openSnackbar(t('Error_globalErrorMessage'), 6, 'topRight', 'failure')
      setServiceUnavailable(true)
    }
  }, [])
  const fetchProvinces = useCallback(
    async (country: 'US' | 'CA') => {
      try {
        const { data } = await getProvinces(country)
        setProvinces(data)
        setCurrentHint(msgToBeDisplay.selectStateProvince)
      } catch (e) {
        openSnackbar(t('Error_globalErrorMessage'), 6, 'topRight', 'failure')
        setServiceUnavailable(true)
      }
    },
    [msgToBeDisplay.selectStateProvince]
  )
  const fetchInstitutes = useCallback(
    async (country: string, province: string | undefined = undefined) => {
      try {
        const { data } = await getInstitutions(country, province)
        setInstitutions(data)
        setCurrentHint(msgToBeDisplay.selectSchool)
      } catch (e) {
        openSnackbar(t('Error_globalErrorMessage'), 6, 'topRight', 'failure')
        setServiceUnavailable(true)
      }
    },
    [msgToBeDisplay.selectSchool]
  )

  const fetchWebStores = useCallback(async (institutionId: string, country: string) => {
    try {
      const { data } = await getInstitutionWebStore(institutionId, country)
      // const mock = [
      //   {
      //     AllowUnauthenticatedBrowsing: true,
      //     FriendlyWebStoreUrl: null,
      //     InstitutionID: '2eaa2118-58be-de11-886d-0030487d8897',
      //     ProductMajorVersions: null,
      //     WebStoreID: '3aaa2118-58be-de11-886d-0030487d8897',
      //     WebStoreName: 'Carleton University - Electronics',
      //     WebStoreUrl: 'https://e5.onthehub.com/WebStore/Welcome.aspx?ws=3aaa2118-58be-de11-886d-0030487d8897',
      //   },
      //   {
      //     AllowUnauthenticatedBrowsing: true,
      //     FriendlyWebStoreUrl: null,
      //     InstitutionID: '1ee18db7-90c4-de11-886d-0030487d8897',
      //     ProductMajorVersions: null,
      //     WebStoreID: '872186bd-90c4-de11-886d-0030487d8897',
      //     WebStoreName: 'Carleton University - School of Computer Science - VMWARE',
      //     WebStoreUrl: 'https://e5.onthehub.com/WebStore/Welcome.aspx?ws=872186bd-90c4-de11-886d-0030487d8897',
      //   },
      //   {
      //     AllowUnauthenticatedBrowsing: true,
      //     FriendlyWebStoreUrl: null,
      //     InstitutionID: '8ffb5d36-6d6f-e011-9c77-b8ac6f0ecbe1',
      //     ProductMajorVersions: null,
      //     WebStoreID: 'f6b40d5a-1870-e011-971f-0030487d8897',
      //     WebStoreName: 'Carleton University - Systems and Computer Engineering',
      //     WebStoreUrl: 'https://e5.onthehub.com/WebStore/Welcome.aspx?ws=f6b40d5a-1870-e011-971f-0030487d8897',
      //   },
      //   {
      //     AllowUnauthenticatedBrowsing: false,
      //     FriendlyWebStoreUrl: null,
      //     InstitutionID: institutionId,
      //     ProductMajorVersions: null,
      //     WebStoreID: '776ef84e-9446-e511-940f-b8ca3a5db7a1',
      //     WebStoreName: 'Carleton University',
      //     WebStoreUrl: 'https://e5.onthehub.com/WebStore/Welcome.aspx?ws=776ef84e-9446-e511-940f-b8ca3a5db7a1',
      //   },
      // ]
      // console.log({ webstores: data })
      setWebstores(data)
    } catch (e) {
      openSnackbar(t('Error_globalErrorMessage'), 6, 'topRight', 'failure')
      setServiceUnavailable(true)
    }
  }, [])

  useEffect(() => {
    Promise.all([fetchCountries()])
  }, [])

  useEffect(() => {
    if (isProvinceIncludedCountry()) {
      Promise.all([fetchProvinces(countryInput.value as ProvinceIncludedCountry)])
    } else if (countryInput.value) {
      Promise.all([fetchInstitutes(countryInput.value)])
    } else if (!countryInput.value) {
      setProvinceInput(DEFAULT_SELECTION)
      setInstitutionInput(DEFAULT_SELECTION)
      setDepartmentInput(DEFAULT_SELECTION)
      setWebstores([])
      setCurrentHint(msgToBeDisplay.selectCountry)
    }
    // }
  }, [countryInput.value, isProvinceIncludedCountry])

  useEffect(() => {
    if (!!provinceInput.value && !!countryInput.value) {
      Promise.all([fetchInstitutes(countryInput.value, provinceInput.value)])
    } else if (isProvinceIncludedCountry() && !provinceInput.value) {
      setInstitutionInput(DEFAULT_SELECTION)
      setDepartmentInput(DEFAULT_SELECTION)
      setWebstores([])
      setCurrentHint(msgToBeDisplay.selectStateProvince)
    }
  }, [countryInput.value, provinceInput.value, isProvinceIncludedCountry()])

  useEffect(() => {
    if (countryInput.value) {
      if (institutionInput.value) {
        Promise.all([fetchWebStores(institutionInput.value, countryInput.value)])
      } else if (!institutionInput.value) {
        setDepartmentInput(DEFAULT_SELECTION)
        setWebstores([])
        setCurrentHint(msgToBeDisplay.selectSchool)
      }
    }
  }, [countryInput.value, institutionInput.value])

  useEffect(() => {
    if (!!countryInput.label && !!institutionInput.label) {
      if (webstores.length > 1) setCurrentHint(msgToBeDisplay.department)
      else if (webstores.length === 1) setDepartmentInput({ value: webstores[0].WebStoreUrl, label: webstores[0].WebStoreName })
      else if (!webstores.length) setCurrentHint(msgToBeDisplay.schoolNoWebStore)
    }
  }, [webstores, countryInput.label, institutionInput.label])

  useEffect(() => {
    if (institutionInput.label) {
      if (departmentInput.value) {
        setCurrentHint(msgToBeDisplay.schoolWebstore(departmentInput))
      } else if (!departmentInput.value) {
        setCurrentHint(msgToBeDisplay.department)
      }
    }
  }, [departmentInput.value, institutionInput.value])

  return (
    <>
      {!serviceUnavailable ? (
        <form className="find-my-school-page-content-wrapper">
          <div className="find-my-school-page-row-wrapper find-my-school-page-hint">{currentHint}</div>
          <div className="find-my-school-page-row-wrapper">
            <label htmlFor="find-my-school-country" className="heavy find-my-school-page-title">
              Country
            </label>
            <div className="find-my-school-page-input-wrapper">
              <Select
                value={countryInput}
                options={countries.map((c) => ({ value: c.CountryIsoAlpha2Code, label: c.CountryName }))}
                className="find-my-school-page-input"
                id="find-my-school-country"
                isSearchable={true}
                isClearable={true}
                onChange={handleCountryChange}
                aria-label="Country"
              />
            </div>
          </div>

          <div className="find-my-school-page-row-wrapper">
            <label htmlFor="find-my-school-state-province" className="heavy find-my-school-page-title">
              State/Province
            </label>
            <div className="find-my-school-page-input-wrapper">
              <Select
                value={provinceInput}
                options={provinces.map((p) => ({ value: p.ProvinceCode, label: p.ProvinceName }))}
                className="find-my-school-page-input"
                id="find-my-school-state-province"
                isSearchable={true}
                isClearable={true}
                isDisabled={!isProvinceIncludedCountry()}
                onChange={handleProvinceChange}
                aria-label="State/Province"
              />
            </div>
          </div>

          <div className="find-my-school-page-row-wrapper">
            <label htmlFor="find-my-school-school" className="heavy find-my-school-page-title">
              School
            </label>
            <div className="find-my-school-page-input-wrapper">
              <Select
                aria-label="School"
                value={institutionInput}
                options={institutions.map((ins) => ({ value: ins.InstitutionID, label: ins.OrganizationName }))}
                className="find-my-school-page-input"
                id="find-my-school-school"
                isSearchable={true}
                isClearable={true}
                isDisabled={
                  isProvinceIncludedCountry() ? !(isProvinceIncludedCountry() && provinceInput.value) : !isProvinceIncludedCountry(false)
                }
                onChange={handleSchoolChange}
              />
            </div>
          </div>
          {!!webstores.length && (
            <div className="find-my-school-page-row-wrapper fade-in">
              <label htmlFor="find-my-school-department" className="heavy find-my-school-page-title">
                Department
              </label>
              <div className="find-my-school-page-input-wrapper">
                <Select
                  aria-label="Department"
                  value={departmentInput}
                  options={webstores.map((ws) => ({ value: ws.WebStoreUrl, label: ws.WebStoreName }))}
                  className="find-my-school-page-input"
                  id="find-my-school-department"
                  isSearchable={true}
                  isClearable={true}
                  isDisabled={!institutionInput.value && !webstores.length}
                  onChange={handleDepartmentChange}
                />
              </div>
            </div>
          )}
        </form>
      ) : (
        <div className="service-unavailable">
          <div className="service-unavailable-icon-wrapper">
            <i className="feather-x-circle service-unavailable-icon" />
          </div>
          <div className="service-unavailable-content">{t('School_schoolFinderError_message')}</div>
        </div>
      )}
      {!countries.length && !serviceUnavailable && <TailLoader />}
    </>
  )
}

export default FindMySchoolForm
